'use client'

import { useState } from 'react'
import EventGallery from '@/components/EventGallery'
import EventSiteSlider from '@/components/EventSiteSlider'
import EventHighlightsIntro from '@/components/EventHighlightsIntro'
import { normalizeGallery, type EventGalleryShape } from '@/lib/galleryShape'
import { normalizeSiteSlider, type EventSiteSliderShape } from '@/lib/siteSliderShape'

/**
 * Une soirée archivée présentée en bandeau, dépliable.
 *
 * La plus récente des archives s'affiche en entier, à sa place habituelle
 * dans la page ; les précédentes prennent cette forme repliée. Sans quoi la
 * page d'accueil s'allongerait indéfiniment — une seule galerie du Bal
 * compte plus de quatre cents photos.
 *
 * Le contenu n'est PAS rendu tant qu'on n'a pas déplié : il est chargé à la
 * demande depuis l'API publique. Le servir replié depuis le serveur
 * alourdirait la page de centaines d'images invisibles, ce qui reviendrait à
 * annuler le bénéfice du repli.
 *
 * Le dessin reprend l'intertitre existant du site — sur-titre or espacé,
 * filet fin — pour ne rien introduire de nouveau dans la page.
 */

const ADMIN_API = (process.env.NEXT_PUBLIC_ADMIN_API_URL || 'https://admin.example.com').replace(/\/$/, '')

export interface ArchiveRepliéeProps {
  slug: string
  /** Titre affiché sur le bandeau — le nom de la soirée. */
  titre: string
  /** Textes d'introduction propres à cette archive. */
  intro?: { eyebrow?: string; title?: string; subtitle?: string }
}

export default function ArchiveRepliee({ slug, titre, intro }: ArchiveRepliéeProps) {
  const [ouvert, setOuvert] = useState(false)
  const [chargement, setChargement] = useState(false)
  const [erreur, setErreur] = useState(false)
  const [galerie, setGalerie] = useState<EventGalleryShape | null>(null)
  const [slider, setSlider] = useState<EventSiteSliderShape>([])

  const basculer = async () => {
    if (ouvert) {
      setOuvert(false)
      return
    }
    setOuvert(true)
    /* Déjà chargé : on ne redemande pas au serveur à chaque ouverture. */
    if (galerie || chargement) return

    setChargement(true)
    setErreur(false)
    try {
      const [rg, rs] = await Promise.all([
        fetch(`${ADMIN_API}/api/public/event/${slug}/gallery`),
        fetch(`${ADMIN_API}/api/public/event/${slug}/site-slider`),
      ])
      const dg = rg.ok ? await rg.json() : null
      const ds = rs.ok ? await rs.json() : null
      setGalerie(normalizeGallery(dg?.event?.gallery))
      setSlider(normalizeSiteSlider(ds?.event?.siteSliderImages))
    } catch {
      setErreur(true)
    } finally {
      setChargement(false)
    }
  }

  return (
    <section className="bg-brand-cream">
      <button
        type="button"
        onClick={basculer}
        aria-expanded={ouvert}
        className="w-full text-center"
        style={{
          background: 'transparent',
          border: 'none',
          borderTop: '1px solid color-mix(in srgb, var(--color-brand-ink) 18%, transparent)',
          cursor: 'pointer',
          padding: 'clamp(36px, 5vw, 60px) 16px',
        }}
      >
        <span
          className="text-brand-ink/70 uppercase font-medium"
          style={{
            display: 'block',
            fontFamily: 'var(--font-body, inherit)',
            fontSize: 'clamp(11px, 0.6875rem + 0.15vw, 13px)',
            letterSpacing: '0.4em',
          }}
        >
          {intro?.eyebrow?.trim() || 'Archive'}
        </span>
        <span
          className="italic font-normal text-brand-ink/95"
          style={{
            display: 'block',
            marginTop: 14,
            fontFamily: 'var(--font-heading, inherit)',
            fontWeight: 400,
            fontSize: 'clamp(1.4rem, 1rem + 1.6vw, 2.2rem)',
            letterSpacing: '-0.01em',
          }}
        >
          {titre}
        </span>
        <span
          aria-hidden
          style={{
            display: 'block',
            width: 32,
            height: 1,
            background: 'color-mix(in srgb, var(--color-brand-ink) 50%, transparent)',
            margin: '18px auto 0',
          }}
        />
        <span
          className="text-brand-ink/60 uppercase"
          style={{
            display: 'block',
            marginTop: 14,
            fontFamily: 'var(--font-body, inherit)',
            fontSize: 'clamp(10px, 0.6rem + 0.1vw, 12px)',
            letterSpacing: '0.22em',
          }}
        >
          {ouvert ? 'Replier' : 'Voir les images'}
        </span>
      </button>

      {ouvert && (
        <div>
          {chargement && (
            <p
              className="text-brand-ink/50 text-center"
              style={{ padding: '0 16px 48px', fontSize: 13, letterSpacing: '0.1em' }}
            >
              Chargement…
            </p>
          )}
          {erreur && (
            <p
              className="text-brand-ink/50 text-center"
              style={{ padding: '0 16px 48px', fontSize: 13 }}
            >
              Les images de cette soirée n&apos;ont pas pu être chargées.
            </p>
          )}
          {!chargement && !erreur && galerie && (
            <>
              <EventHighlightsIntro content={intro} />
              {slider.length > 0 && <EventSiteSlider images={slider} />}
              <EventGallery gallery={galerie} highlights={slider} />
            </>
          )}
        </div>
      )}
    </section>
  )
}
